import { ReactElement } from 'react';
import { Chip, makeStyles } from '@material-ui/core';
import { Theme, useTheme } from '@material-ui/core/styles';
import { SparklesIcon } from '../icons/sparkles-icon';
import { RemediationSummary } from '@internal/yardstick-common';

const useStyles = makeStyles<Theme>(theme => ({
  chip: {
    marginLeft: '.25em',
    marginBottom: '.25em',
    height: 'auto',
    padding: theme.spacing(0, 1.25),
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(227, 245, 253, 0.16)' : '#E3F5FD',
    color:
      theme.palette.type === 'dark' ? '#B3DCF3' : theme.palette.text.primary,
  },
  chipActionNeeded: {
    marginLeft: '.25em',
    marginBottom: '.25em',
    height: 'auto',
    padding: theme.spacing(0, 1.25),
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(0, 128, 128, 0.2)' : '#E6F7F5',
    color: theme.palette.type === 'dark' ? '#80CBC4' : '#00695C',
  },
  chipFailed: {
    marginLeft: '.25em',
    marginBottom: '.25em',
    height: 'auto',
    padding: theme.spacing(0, 1.25),
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(244, 67, 54, 0.16)' : '#FEECEB',
    color: theme.palette.type === 'dark' ? '#EF9A9A' : '#C62828',
  },
  label: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '12px',
    fontWeight: 700,
    textTransform: 'none',
    color: 'inherit',
    padding: 0,
  },
}));

export interface AutoRemediationBadgeProps {
  remediationSummary?: RemediationSummary;
}

function getActionLabel(summary: RemediationSummary): string {
  if (summary.status === 'action_needed' && summary.action) {
    switch (summary.action.type) {
      case 'pull_request':
        return 'PR ready for review';
      case 'github_issue':
        return 'Issue created';
      case 'external_link':
        return summary.action.label;
      default:
        return '';
    }
  }
  return '';
}

export const AutoRemediationBadge = ({
  remediationSummary,
}: AutoRemediationBadgeProps): ReactElement => {
  const classes = useStyles();
  const theme = useTheme<Theme>();
  const iconColor =
    theme.palette.type === 'dark' ? '#B3DCF3' : theme.palette.primary.dark;
  const actionIconColor = theme.palette.type === 'dark' ? '#80CBC4' : '#00695C';
  const failedIconColor = theme.palette.type === 'dark' ? '#EF9A9A' : '#C62828';

  const status = remediationSummary?.status ?? 'available';

  if (status === 'action_needed') {
    const label = getActionLabel(remediationSummary!);
    return (
      <Chip
        size="small"
        data-testid="auto-remediation-badge-action-needed"
        role="status"
        aria-label={label}
        classes={{ root: classes.chipActionNeeded, label: classes.label }}
        label={
          <>
            <SparklesIcon color={actionIconColor} size={12} />
            {label}
          </>
        }
      />
    );
  }

  if (status === 'in_progress') {
    return (
      <Chip
        size="small"
        data-testid="auto-remediation-badge-in-progress"
        role="status"
        aria-label="Auto remediation in progress"
        classes={{ root: classes.chip, label: classes.label }}
        label={
          <>
            <SparklesIcon color={iconColor} size={12} />
            Auto remediation in progress
          </>
        }
      />
    );
  }

  if (status === 'failed') {
    return (
      <Chip
        size="small"
        data-testid="auto-remediation-badge-failed"
        role="status"
        aria-label="Auto remediation failed"
        classes={{ root: classes.chipFailed, label: classes.label }}
        label={
          <>
            <SparklesIcon color={failedIconColor} size={12} />
            Auto remediation failed
          </>
        }
      />
    );
  }

  return (
    <Chip
      size="small"
      data-testid="auto-remediation-badge"
      role="status"
      aria-label="Auto remediation available"
      classes={{ root: classes.chip, label: classes.label }}
      label={
        <>
          <SparklesIcon color={iconColor} size={12} />
          Auto remediation
        </>
      }
    />
  );
};
